import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrentUser } from './store/authSlice';

// Layouts — kept eager since they wrap every route
import MainLayout from './layouts/MainLayout';
import DashboardLayout from './layouts/DashboardLayout';
import AdminLayout from './layouts/AdminLayout';

// Landing page — eager (critical path)
import Landing from './pages/Landing';

// Public pages — lazy loaded
const Features = lazy(() => import('./pages/Features'));
const FeatureDetail = lazy(() => import('./pages/FeatureDetail'));
const UseCases = lazy(() => import('./pages/UseCases'));
const UseCaseDetail = lazy(() => import('./pages/UseCaseDetail'));
const Contact = lazy(() => import('./pages/Contact'));
const Faq = lazy(() => import('./pages/Faq'));
const Pricing = lazy(() => import('./pages/Pricing'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const EmailVerifyRedirect = lazy(() => import('./pages/EmailVerifyRedirect'));
const PublicMenu = lazy(() => import('./pages/PublicMenu'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PayAtTable = lazy(() => import('./pages/PayAtTable'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const CookiesPolicy = lazy(() => import('./pages/CookiesPolicy'));
const PaymentOptions = lazy(() => import('./pages/PaymentOptions'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Dashboard pages — lazy loaded
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MenuList = lazy(() => import('./pages/MenuList'));
const MenuEdit = lazy(() => import('./pages/MenuEdit'));
const MenuCreate = lazy(() => import('./pages/MenuCreate'));
const DishManage = lazy(() => import('./pages/DishManage'));
const DishesIndex = lazy(() => import('./pages/DishesIndex'));
const CategoriesIndex = lazy(() => import('./pages/CategoriesIndex'));
const MenuBuilder = lazy(() => import('./pages/MenuBuilder'));
const OptionGroups = lazy(() => import('./pages/OptionGroups'));
const MenuWoltSync = lazy(() => import('./pages/MenuWoltSync'));
const CategoryManage = lazy(() => import('./pages/CategoryManage'));
const QrManager = lazy(() => import('./pages/QrManager'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Translations = lazy(() => import('./pages/Translations'));
const Orders = lazy(() => import('./pages/Orders'));
const ArchivedOrders = lazy(() => import('./pages/ArchivedOrders'));
const OrdersExports = lazy(() => import('./pages/OrdersExports'));
const Customers = lazy(() => import('./pages/Customers'));
const MenuBranding = lazy(() => import('./pages/MenuBranding'));
const TableManagement = lazy(() => import('./pages/TableManagement'));
const ReservationsPage = lazy(() => import('./pages/ReservationsPage'));
const TableBoard = lazy(() => import('./pages/TableBoard'));
const TablesIndex = lazy(() => import('./pages/TablesIndex'));
const Profile = lazy(() => import('./pages/Profile'));
const AadePage = lazy(() => import('./pages/AadePage'));
const AadeBillingErrorsPage = lazy(() => import('./pages/AadeBillingErrorsPage'));
const PrintersPage = lazy(() => import('./pages/PrintersPage'));
const Employees = lazy(() => import('./pages/Employees'));
const EmployeeDashboardPage = lazy(() => import('./pages/EmployeeDashboard'));
const MyShift = lazy(() => import('./pages/MyShift'));
const KdsPage = lazy(() => import('./pages/KdsPage'));
const CompanySettings = lazy(() => import('./pages/CompanySettings'));
const ThirdPartySettings = lazy(() => import('./pages/ThirdPartySettings'));
const WoltSettings = lazy(() => import('./pages/WoltSettings'));
const WoltOAuthCallback = lazy(() => import('./pages/WoltOAuthCallback'));
const SmtpSettings = lazy(() => import('./pages/SmtpSettings'));
const WifiQrCodesPage = lazy(() => import('./pages/WifiQrCodesPage'));
const WidgetPage = lazy(() => import('./pages/WidgetPage'));
const MediaPage = lazy(() => import('./pages/MediaPage'));
const CreateOrder = lazy(() => import('./pages/CreateOrder'));

// Admin pages — lazy loaded
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminMenus = lazy(() => import('./pages/admin/AdminMenus'));
const AdminSeatingAreas = lazy(() => import('./pages/admin/AdminSeatingAreas'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'));
const AdminPrinters = lazy(() => import('./pages/admin/AdminPrinters'));

// Guards
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import GoogleAnalyticsTracker from './components/GoogleAnalyticsTracker';

/**
 * Root application component with route definitions.
 */
function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { initialized } = useSelector((state) => state.auth);

  useEffect(() => {
    const protectedPrefixes = [
      '/dashboard', '/onboarding', '/analytics', '/orders', '/customers', '/menus', '/dishes',
      '/categories', '/option-groups', '/tables', '/locations', '/aade', '/subscription',
      '/printers', '/employees', '/duties', '/employee-dashboard', '/my-shift', '/kds',
      '/profile', '/company', '/settings', '/widget', '/media', '/admin',
    ];
    const isProtectedPath = protectedPrefixes.some(
      (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
    );
    const hasFallbackToken = Boolean(sessionStorage.getItem('auth_token'));

    if (!initialized && (isProtectedPath || hasFallbackToken)) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, initialized, location.pathname]);

  return (
    <>
      <GoogleAnalyticsTracker />
      <Suspense fallback={null}>
      <Routes>
        {/* Public routes */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<Landing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/features/:featureSlug" element={<FeatureDetail />} />
          <Route path="/third-party-integrations" element={<Navigate to="/features/third-party-integrations" replace />} />
          <Route path="/use-cases" element={<UseCases />} />
          <Route path="/use-cases/:useCaseSlug" element={<UseCaseDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/email/verify/:id/:hash" element={<EmailVerifyRedirect />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          <Route path="/payment-options" element={<PaymentOptions />} />
        </Route>

        {/* Greek locale-prefixed public routes (SEO crawlable) */}
        <Route path="/el" element={<MainLayout />}>
          <Route index element={<Landing />} />
          <Route path="features" element={<Features />} />
          <Route path="features/:featureSlug" element={<FeatureDetail />} />
          <Route path="third-party-integrations" element={<Navigate to="/el/features/third-party-integrations" replace />} />
          <Route path="use-cases" element={<UseCases />} />
          <Route path="use-cases/:useCaseSlug" element={<UseCaseDetail />} />
          <Route path="contact" element={<Contact />} />
          <Route path="faq" element={<Faq />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<TermsOfUse />} />
          <Route path="cookies" element={<CookiesPolicy />} />
          <Route path="payment-options" element={<PaymentOptions />} />
        </Route>

        {/* Public menu view (no layout chrome) */}
        <Route path="/m/:slug" element={<PublicMenu />} />
        <Route path="/order/success" element={<PaymentSuccess />} />
        <Route element={<MainLayout />}>
          <Route path="/order/pay-at-table" element={<PayAtTable />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Full-screen auth routes (no sidebar chrome) */}
        <Route element={<PrivateRoute />}>
          <Route path="/kds" element={<KdsPage />} />
          <Route path="/orders/create" element={<CreateOrder />} />
        </Route>

        {/* Protected dashboard routes */}
        <Route element={<PrivateRoute />}>
          <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/archived" element={<ArchivedOrders />} />
            <Route path="/orders/exports" element={<OrdersExports />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/menus" element={<MenuList />} />
            <Route path="/dishes" element={<DishesIndex />} />
            <Route path="/categories" element={<CategoriesIndex />} />
            <Route path="/menus/create" element={<MenuCreate />} />
            <Route path="/menus/:menuId/edit" element={<MenuEdit />} />
            <Route path="/option-groups" element={<OptionGroups />} />
            <Route path="/menus/:menuId/categories" element={<CategoryManage />} />
            <Route path="/menus/:menuId/builder" element={<MenuBuilder />} />
            <Route path="/menus/:menuId/wolt-sync" element={<MenuWoltSync />} />
            <Route path="/menus/:menuId/qr" element={<QrManager />} />
            <Route path="/menus/:menuId/translations" element={<Translations />} />
            <Route path="/menus/:menuId/branding" element={<MenuBranding />} />
            <Route path="/menus/:menuId/dishes/new" element={<DishManage editorMode />} />
            <Route path="/menus/:menuId/dishes/:dishId/edit" element={<DishManage editorMode />} />
            <Route path="/menus/:menuId/dishes" element={<DishManage />} />
            <Route path="/tables" element={<TablesIndex />} />
            <Route path="/locations/:locationId/tables" element={<TableManagement />} />
            <Route path="/locations/:locationId/reservations" element={<ReservationsPage />} />
            <Route path="/locations/:locationId/table-board" element={<TableBoard />} />
            <Route path="/aade" element={<AadePage />} />
            <Route path="/aade/billing-errors" element={<AadeBillingErrorsPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/printers" element={<PrintersPage />} />
            <Route path="/settings/printers" element={<PrintersPage />} />
            <Route path="/employees" element={<Employees />} />
            <Route path="/duties" element={<Navigate to="/employees" replace />} />
            <Route path="/employee-dashboard" element={<EmployeeDashboardPage />} />
            <Route path="/my-shift" element={<MyShift />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/company" element={<CompanySettings />} />
            <Route path="/settings/third-party" element={<ThirdPartySettings />} />
            <Route path="/settings/wolt" element={<WoltSettings />} />
            <Route path="/settings/wolt/callback" element={<WoltOAuthCallback />} />
            <Route path="/settings/smtp" element={<SmtpSettings />} />
            <Route path="/settings/wifi-qr" element={<WifiQrCodesPage />} />
            <Route path="/widget" element={<WidgetPage />} />
            <Route path="/media" element={<MediaPage />} />
          </Route>
        </Route>

        {/* Super Admin routes */}
        <Route element={<AdminRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/menus" element={<AdminMenus />} />
            <Route path="/admin/seating-areas" element={<AdminSeatingAreas />} />
            <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
            <Route path="/admin/printers" element={<AdminPrinters />} />
          </Route>
        </Route>
      </Routes>
      </Suspense>
    </>
  );
}

export default App;
